export const SUBSCRIPTION_CATEGORIES = [
  'Domain',
  'Hosting',
  'Cloud Server',
  'Cloud Database',
  'Email',
  'Software/SaaS',
  'AI/API Service',
  'Security',
  'Backup',
  'Statutory/Compliance Service',
  'Rental Asset',
  'AMC/Support',
  'Internet/Telecom',
  'Professional Membership',
  'Other',
]

export const BILLING_MODELS = [
  'Fixed recurring',
  'Usage-based',
  'Fixed plus usage',
  'Per-user/per-seat',
  'Per-resource',
  'Rental',
  'One-time renewable',
  'Auto-renewing contract',
]

export const SUBSCRIPTION_STATUSES = [
  'Draft',
  'Pending Approval',
  'Approved',
  'Active',
  'Trial',
  'Pending',
  'Renewal Due',
  'Renewal Under Review',
  'Pending Payment',
  'Renewed',
  'Expiring Soon',
  'Expired',
  'Suspended',
  'Cancellation Requested',
  'Cancelled',
  'Rental Return Due',
  'Closed',
]

export const BILLING_CYCLES = [
  'Monthly',
  'Quarterly',
  'Half-yearly',
  'Semi-Annual',
  'Annual',
  'Multi-year',
  'Usage-based',
  'Custom',
]

export const CRITICALITIES = ['Critical', 'High', 'Medium', 'Low']

export const PAYMENT_METHODS = [
  'Credit Card',
  'Corporate Card',
  'Bank Transfer',
  'Invoice',
  'UPI',
  'Auto-debit',
  'Other',
]

export const PAYMENT_STATUSES = [
  'Expected',
  'Invoice awaited',
  'Invoice received',
  'Under verification',
  'Approved',
  'Due',
  'Paid',
  'Partially paid',
  'Overdue',
  'Disputed',
  'Waived',
  'Cancelled',
  'Pending',
  'Failed',
  'Refunded',
]

export const RENEWAL_DECISIONS = [
  'Pending decision',
  'Renew',
  'Renew with changed plan',
  'Reduce licences/resources',
  'Negotiate',
  'Cancel',
  'Allow to expire',
]

export const RESOURCE_TYPES = [
  'Domain',
  'SSL',
  'Hosting',
  'Email',
  'Droplet',
  'Database',
  'Volume',
  'Backup',
  'Reserved IP',
  'Cluster',
  'Organisation',
  'Project',
  'Licence seat',
  'Rental asset',
  'Other',
]

export const ENVIRONMENTS = ['Production', 'UAT', 'Development', 'Demo', 'Internal']

export const DOCUMENT_TYPES = [
  'Vendor agreement',
  'Order form',
  'Latest invoice',
  'Payment receipt',
  'Renewal quotation',
  'Purchase order',
  'GST invoice',
  'Rental agreement',
  'Asset handover',
  'Cancellation',
  'Other',
]

export const REMINDER_TRIGGER_BASES = [
  { value: 'renewal_date', label: 'Renewal date' },
  { value: 'expiry_date', label: 'Expiry date' },
  { value: 'payment_due_date', label: 'Payment due date' },
  { value: 'notice_deadline', label: 'Notice/cancellation deadline' },
  { value: 'rental_return_date', label: 'Rental return date' },
  { value: 'contract_end_date', label: 'Contract end date' },
  { value: 'custom_date', label: 'Custom date' },
]

export const EXPIRY_WINDOW_OPTIONS = [
  { value: 7, label: 'Expiring in 7 days' },
  { value: 15, label: 'Expiring in 15 days' },
  { value: 30, label: 'Expiring in 30 days' },
  { value: 60, label: 'Expiring in 60 days' },
  { value: 90, label: 'Expiring in 90 days' },
  { value: -1, label: 'Already expired' },
]

export const formatCurrency = (amount, currency = 'INR') => {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0)
  } catch {
    return `${currency || 'INR'} ${Number(amount || 0).toFixed(2)}`
  }
}

export const formatDate = (value) => {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** CSS modifier for subscription status badges (Renewal Due → red). */
export const getSubscriptionStatusClass = (status) => {
  const raw = String(status || '').trim()
  if (!raw) return 'sub-status--default'
  const slug = raw.toLowerCase().replace(/\s+/g, '-')
  const known = new Set([
    'active', 'approved', 'renewed', 'trial', 'pending', 'draft', 'pending-approval',
    'renewal-due', 'renewal-under-review', 'pending-payment', 'expiring-soon',
    'rental-return-due', 'expired', 'suspended', 'cancellation-requested', 'cancelled', 'closed',
  ])
  return known.has(slug) ? `sub-status--${slug}` : 'sub-status--default'
}

export const getPaymentStatusClass = (status) => {
  const map = {
    Paid: 'sub-status--active',
    'Partially paid': 'sub-status--active',
    Approved: 'sub-status--approved',
    Expected: 'sub-status--pending',
    Pending: 'sub-status--pending',
    'Invoice awaited': 'sub-status--pending',
    'Invoice received': 'sub-status--pending',
    'Under verification': 'sub-status--pending',
    Due: 'sub-status--renewal-due',
    Overdue: 'sub-status--expired',
    Disputed: 'sub-status--expired',
    Failed: 'sub-status--expired',
    Waived: 'sub-status--closed',
    Cancelled: 'sub-status--cancelled',
    Refunded: 'sub-status--closed',
  }
  return map[status] || 'sub-status--default'
}

export const getCriticalityClass = (criticality) => {
  const c = String(criticality || '').toLowerCase()
  if (c === 'critical') return 'sub-crit--critical'
  if (c === 'high') return 'sub-crit--high'
  if (c === 'low') return 'sub-crit--low'
  return 'sub-crit--medium'
}

/** Best available monthly amount for list display (original currency). */
export const resolveMonthlyCost = (row = {}) => {
  const monthly = Number(row.monthlyCost)
  if (Number.isFinite(monthly) && monthly > 0) return monthly

  const ub = Number(row.usageBudget?.estimatedMonthlyCost)
  if (Number.isFinite(ub) && ub > 0) return ub

  const usage = Number(row.expectedUsageAmount)
  if (Number.isFinite(usage) && usage > 0) return usage

  const cycle = row.billingCycle || ''
  const annual = Number(row.annualCost) || Number(row.budgetedAnnualCost) || 0
  if (annual > 0 && (cycle === 'Annual' || cycle === 'Multi-year')) {
    return Math.round((annual / 12) * 100) / 100
  }

  const base = Number(row.baseRecurringAmount)
  if (Number.isFinite(base) && base > 0) {
    if (cycle === 'Annual') return Math.round((base / 12) * 100) / 100
    if (cycle === 'Quarterly') return Math.round((base / 3) * 100) / 100
    return base
  }
  return 0
}

export const resolveInrEquivalent = (row = {}, monthlyAmount) => {
  const amount = monthlyAmount ?? resolveMonthlyCost(row)
  const currency = String(row.currency || 'INR').toUpperCase()
  if (currency === 'INR' || !amount) return null
  const rate = Number(row.exchangeRate)
  if (!Number.isFinite(rate) || rate <= 0) return null
  return Math.round(amount * rate * 100) / 100
}

export const daysRemainingClass = (days) => {
  if (days == null || Number.isNaN(Number(days))) return 'sub-days--ok'
  const d = Number(days)
  if (d < 0) return 'sub-days--overdue'
  if (d <= 7) return 'sub-days--urgent'
  if (d <= 15) return 'sub-days--warn'
  return 'sub-days--ok'
}

export const toDateInput = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().split('T')[0]
}

export const emptyReminder = () => ({
  name: 'Expiry reminder',
  enabled: true,
  triggerBase: 'expiry_date',
  daysOffset: -30,
  sendTime: '10:00',
  timezone: 'Asia/Kolkata',
  inApp: true,
  email: true,
  toEmails: [],
  ccEmails: [],
  escalationEmails: [],
  toUserIds: [],
  ccUserIds: [],
  escalationUserIds: [],
  includeOwners: { business: true, technical: true, finance: true },
  subjectTemplate: 'Action Required: {{name}} expires on {{date}} – {{days}} days remaining',
  bodyTemplate: '',
  repeatEveryDays: 0,
  stopWhenActionCompleted: true,
})

export const emptyResource = () => ({
  name: '',
  resourceType: 'Other',
  environment: 'Production',
  status: 'Active',
  domainName: '',
  registrar: '',
  nameServers: '',
  autoRenewal: false,
  registrationDate: '',
  expiryDate: '',
  renewalDate: '',
  renewalCost: 0,
  productOrProject: '',
  monthlyEstimatedCost: 0,
  actualLatestCost: 0,
  startDate: '',
  decommissionDate: '',
  purchasedLicences: 0,
  assignedLicences: 0,
  costPerLicence: 0,
  accountAdministrator: '',
  assetCode: '',
  rentalStartDate: '',
  rentalEndDate: '',
  returnDueDate: '',
  securityDeposit: 0,
  currentCondition: '',
  returnStatus: 'Not due',
  remarks: '',
  reminders: [],
})

export const emptyForm = () => ({
  subscriptionNumber: '',
  subscriptionName: '',
  description: '',
  internalReference: '',
  vendorAccountId: '',
  planName: '',
  businessPurpose: '',
  provider: '',
  vendorName: '',
  vendorLegalEntity: '',
  category: 'Software/SaaS',
  billingModel: 'Fixed recurring',
  status: 'Draft',
  criticality: 'Medium',
  department: '',
  projectName: '',
  costCentre: '',
  startDate: new Date().toISOString().split('T')[0],
  currentPeriodStart: '',
  currentPeriodEnd: '',
  renewalDate: '',
  endDate: '',
  expiryDate: '',
  contractEndDate: '',
  noticePeriodDays: 0,
  noticeDeadline: '',
  minimumCommitmentEnd: '',
  billingCycle: 'Monthly',
  autoRenewal: false,
  autoPayment: false,
  gracePeriodDays: 0,
  customBillingSchedule: '',
  currency: 'INR',
  baseRecurringAmount: 0,
  expectedUsageAmount: 0,
  expectedTotalAmount: 0,
  lastBilledAmount: 0,
  taxAmount: 0,
  totalAmount: 0,
  exchangeRate: 1,
  inrEquivalent: 0,
  budgetedAnnualCost: 0,
  actualYtdCost: 0,
  monthlyCost: 0,
  annualCost: 0,
  seats: 0,
  purchasedLicences: 0,
  assignedLicences: 0,
  paymentMethod: 'Invoice',
  paymentInstrument: '',
  paymentTerms: '',
  purchaseOrderNumber: '',
  contractNumber: '',
  securityDeposit: 0,
  refundableDeposit: false,
  gst: {
    indianGstApplicable: false,
    vendorGstin: '',
    companyGstin: '',
    foreignVendor: false,
    importOfServices: false,
    rcmApplicable: false,
    rcmRate: 0,
    itcEligible: true,
    expenseLedger: '',
    accountingCategory: '',
    gstVerificationStatus: 'Not required',
    financeRemarks: '',
  },
  usageBudget: {
    estimatedMonthlyCost: 0,
    budgetLimit: 0,
    warningThreshold: 0,
    variancePercentAlert: 20,
    lastActualAmount: 0,
    lastActualMonth: '',
    currency: 'INR',
  },
  usageReview: {
    stillRequired: true,
    currentlyUsed: true,
    purchasedLicences: 0,
    usedLicences: 0,
    unusedLicences: 0,
    resourceUtilisation: '',
    businessDependency: '',
    alternativeVendorAvailable: false,
    downgradePossible: false,
    costOptimisationOpportunity: '',
    recommendedAction: '',
    estimatedSavings: 0,
  },
  renewal: {
    step: 'Renewal identified',
    decision: 'Pending decision',
    requestedAmount: 0,
    approvedAmount: 0,
    proposedRenewalCost: 0,
    approvalStatus: 'Not required',
    quotationReceived: false,
    negotiationStatus: 'Not started',
    paymentStatus: '',
    approvalComments: '',
    rejectionReason: '',
    changesRequested: '',
  },
  resources: [],
  documents: [],
  costAllocations: [],
  reminders: [emptyReminder()],
  notes: '',
})

export const mapSubscriptionToForm = (s) => {
  const base = emptyForm()
  if (!s) return base
  return {
    ...base,
    ...s,
    startDate: toDateInput(s.startDate),
    currentPeriodStart: toDateInput(s.currentPeriodStart),
    currentPeriodEnd: toDateInput(s.currentPeriodEnd),
    renewalDate: toDateInput(s.renewalDate),
    endDate: toDateInput(s.endDate),
    expiryDate: toDateInput(s.expiryDate),
    contractEndDate: toDateInput(s.contractEndDate),
    noticeDeadline: toDateInput(s.noticeDeadline),
    minimumCommitmentEnd: toDateInput(s.minimumCommitmentEnd),
    vendorName: s.vendorName || s.provider || '',
    provider: s.provider || s.vendorName || '',
    gst: { ...base.gst, ...(s.gst || {}) },
    usageBudget: { ...base.usageBudget, ...(s.usageBudget || {}) },
    usageReview: { ...base.usageReview, ...(s.usageReview || {}) },
    renewal: { ...base.renewal, ...(s.renewal || {}) },
    resources: (s.resources || []).map((r) => ({
      ...emptyResource(),
      ...r,
      registrationDate: toDateInput(r.registrationDate),
      expiryDate: toDateInput(r.expiryDate),
      renewalDate: toDateInput(r.renewalDate),
      startDate: toDateInput(r.startDate),
      decommissionDate: toDateInput(r.decommissionDate),
      rentalStartDate: toDateInput(r.rentalStartDate),
      rentalEndDate: toDateInput(r.rentalEndDate),
      returnDueDate: toDateInput(r.returnDueDate),
      asset: r.asset?._id || r.asset || '',
      technicalOwner: r.technicalOwner?._id || r.technicalOwner || '',
      project: r.project?._id || r.project || '',
      reminders: r.reminders || [],
    })),
    documents: s.documents || [],
    costAllocations: s.costAllocations || [],
    reminders: (s.reminders && s.reminders.length) ? s.reminders : [emptyReminder()],
    businessOwner: s.businessOwner?._id || s.businessOwner || '',
    technicalOwner: s.technicalOwner?._id || s.technicalOwner || '',
    financeOwner: s.financeOwner?._id || s.financeOwner || '',
    vendor: s.vendor?._id || s.vendor || '',
    project: s.project?._id || s.project || '',
  }
}

/** Display label for billing / renewal frequency */
export const formatRenewalFrequency = (billingCycle) => {
  const raw = String(billingCycle || '').trim()
  if (!raw) return '—'
  const key = raw.toLowerCase()
  const map = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annual: 'Yearly',
    yearly: 'Yearly',
    'half-yearly': 'Half-yearly',
    'semi-annual': 'Half-yearly',
    'semi annual': 'Half-yearly',
    'multi-year': 'Multi-year',
    'usage-based': 'Usage-based',
    custom: 'Custom',
  }
  return map[key] || raw
}

export const transformSubscriptionRow = (item) => ({
  ...item,
  id: item._id || item.id,
  provider: item.vendorName || item.provider,
})

export const transformPaymentRow = (item) => ({
  ...item,
  id: item._id || item.id,
})

export const extractSubscriptions = (response) => {
  if (!response?.success) return []
  return response.subscriptions || response.data || []
}

export const extractPayments = (response) => {
  if (!response?.success) return []
  return response.payments || response.data || []
}

export const defaultSubscriptionStats = () => ({
  total: 0,
  active: 0,
  trial: 0,
  expired: 0,
  cancelled: 0,
  expiring: 0,
  recent: 0,
  renewed: 0,
  monthlyCost: 0,
  annualCost: 0,
  fyActualCost: 0,
  overduePayments: 0,
  missingInvoices: 0,
  unusedLicences: 0,
  rcmApplicable: 0,
  domains: 0,
  droplets: 0,
  databases: 0,
  rentalAssets: 0,
})

export const defaultPaymentStats = () => ({
  total: 0,
  paid: 0,
  pending: 0,
  failed: 0,
  refunded: 0,
  overdue: 0,
  due: 0,
  invoiceAwaited: 0,
  totalAmount: 0,
})
